/**
 * Readiness vocabulary shared by the closing calendars, the calendar detail
 * drawer, and the drilldown Closing tab (closing-readiness.md §1).
 *
 * The one rule this file exists to enforce: the two pipelines schedule
 * DIFFERENT events. A preclose loan has a *closing* (`mode = "closing"`); an
 * acquisition has a *trade settlement* (`mode = "trade"`). Never render them
 * with one vocabulary — `modeLabel()` / `eventNoun()` pick the words.
 */
import { alpha } from "@mui/material/styles";
import { format } from "date-fns";

import formatDollar from "../../_src_shared/utils/functions/formatDollar";
import {
  convertToEasternTime,
  normalizeIsoStringToUtc,
} from "../../_src_shared/utils/functions/formatDateTime";
import { amber, slate, status, steel, teal } from "../../theme/colors";

// ── Panel palette ────────────────────────────────────────────────────────────
// The restrained light theme the closing calendars, the calendar detail drawer
// and the drilldown Closing tab share; colour is spent almost entirely on
// readiness. (Was calendar-local — lifted here so the panel sections the drawer
// and the Closing tab both render can read it without reaching into a screen.)
export const C = {
  bg: "#f7f8fa",
  surface: "#ffffff",
  ink: "#1a2332",
  sub: "#5b6675",
  faint: "#98a1ad",
  line: "#e6e9ee",
  lineSoft: "#eef1f5",
  accent: "#2451e6",
  accentSoft: "#eef2ff",
  todayBg: "#f5f8ff",
};

// The dashed border of an expected-only (soft) date chip — neutral, so the
// only colour on a soft chip is its edge bar. The chip and the legend both
// draw this line.
export const SOFT_LINE = alpha(slate[500], 0.55);

// Monospace stack for numbers / wire detail (no webfont dependency).
export const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

// ── Readiness levels ─────────────────────────────────────────────────────────
// `signal` is the Capital Markets colour (red / amber / green); `c` / `bg` are
// the closer-facing chip colours from the theme status set.
export const READINESS = {
  not_scheduled: {
    key: "not_scheduled",
    label: "Not scheduled",
    short: "Not scheduled",
    signal: "red",
    // Magenta, not red: same Capital Markets signal as Blocked, different
    // cause (an empty entry vs a hard stop) — the two must tell apart at chip
    // size, and two reds don't.
    c: status.missing.fg,
    bg: status.missing.bg,
  },
  blocked: {
    key: "blocked",
    label: "Blocked",
    short: "Blocked",
    signal: "red",
    c: status.error.fg,
    bg: status.error.bg,
  },
  at_risk: {
    key: "at_risk",
    label: "At risk",
    short: "At risk",
    signal: "amber",
    c: amber.chipText,
    bg: amber.light,
  },
  ready_with_items: {
    key: "ready_with_items",
    label: "Ready with items",
    short: "Items open",
    signal: "amber",
    c: status.warning.fg,
    bg: status.warning.bg,
  },
  ready: {
    key: "ready",
    label: "Ready",
    short: "Ready",
    signal: "green",
    c: status.success.fg,
    bg: status.success.bg,
  },
  closed: {
    key: "closed",
    label: "Closed",
    short: "Closed",
    signal: "green",
    c: slate[500],
    bg: slate[100],
  },
};

// Order the filter chips + summaries follow: worst first, closed last.
export const READINESS_ORDER = [
  "blocked",
  "not_scheduled",
  "at_risk",
  "ready_with_items",
  "ready",
  "closed",
];

export const readinessMeta = (level) =>
  READINESS[level] || READINESS.not_scheduled;

// A card's effective level: closed wins, then the backend's level.
export const cardLevel = (card) =>
  card?.closed ? "closed" : card?.readinessLevel || "not_scheduled";
export const cardLevelMeta = (card) => readinessMeta(cardLevel(card));

// The card's `fundingSignal` (red / amber / green) → solid colour.
export const SIGNAL_COLOR = {
  red: status.error.solid,
  amber: amber.main,
  green: status.success.solid,
};
export const signalColor = (signal) => SIGNAL_COLOR[signal] || slate[400];

// ── Pipeline vocabulary ──────────────────────────────────────────────────────
export const isTrade = (card) =>
  card?.mode === "trade" || card?.pipeline === "acquisition";

// "Closing" / "Trade settlement" — the event noun, capitalised.
export const eventNoun = (card, { plural = false } = {}) =>
  isTrade(card)
    ? plural
      ? "Trade settlements"
      : "Trade settlement"
    : plural
      ? "Closings"
      : "Closing";

// The pipeline status line: `loanStatus`, plus `tradeStatus` on acquisitions.
// Rendered prominently at the top of every card / sidebar — the readiness
// level is secondary to it.
export const pipelineStatusLabel = (card) => {
  const parts = [card?.loanStatus];
  if (isTrade(card) && card?.tradeStatus) parts.push(card.tradeStatus);
  return parts.filter(Boolean).join(" · ") || "—";
};

// What the closed label reads: "Closed" on preclose, "Settled" on a trade.
export const closedLabel = (card) => (isTrade(card) ? "Settled" : "Closed");

// The firm-date field name for the pipeline (for labels like "Scheduled").
export const firmDateLabel = (card) =>
  isTrade(card) ? "Settlement date" : "Scheduled closing";

// `dateBasis` → a short caption for the date shown on the card.
export const DATE_BASIS_LABEL = {
  scheduled: "Scheduled",
  settlement: "Settlement",
  expected: "Expected",
  funded: "Funded",
  none: "No date",
};
export const dateBasisLabel = (card) =>
  DATE_BASIS_LABEL[card?.dateBasis] || DATE_BASIS_LABEL.none;

// A soft placement (no firm date yet) → hollow / dashed chip style.
export const isSoftDate = (card) => card?.dateBasis === "expected";

// ── Card accessors ───────────────────────────────────────────────────────────
export const cardId = (card) => card?.dealId || card?._id;

export const cardDisplayName = (card) => {
  const names = Array.isArray(card?.borrowerNames)
    ? card.borrowerNames.filter(Boolean)
    : [];
  if (names.length === 1) return names[0];
  if (names.length > 1) return `${names[0]} +${names.length - 1}`;
  return card?.loanName || `Loan #${card?.loanNumber ?? "—"}`;
};

export const cardChipLabel = (card) =>
  card?.loanNumber != null
    ? `Loan #${card.loanNumber}`
    : card?.loanName || "Loan";

// The backend labels a property "subject property" when it can't read the
// address off the LOS shape (`address.fullAddress` is a dict there) — treat that
// placeholder as "no address" so a caller can fall back to the loan itself.
const ADDRESS_PLACEHOLDER = /^subject property$/i;
export const cardAddress = (card) =>
  (Array.isArray(card?.propertyAddresses) ? card.propertyAddresses : []).find(
    (a) => a && !ADDRESS_PLACEHOLDER.test(String(a).trim()),
  ) || null;

export const cardAmount = (card) => card?.loanAmount ?? card?.amount ?? null;

export const cardAmountLabel = (card) => {
  const amt = cardAmount(card);
  return amt == null ? null : formatDollar(amt);
};

// Compact dollars for dense surfaces (chips, day totals): $490K, $9.5M.
export const formatCompactDollar = (n) => {
  if (n == null) return null;
  const v = Number(n);
  if (Number.isNaN(v)) return null;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(v >= 1e7 ? 0 : 1)}M`;
  if (v >= 1e3) return `$${Math.round(v / 1e3)}K`;
  return `$${Math.round(v)}`;
};

export const cardAmountCompact = (card) => {
  const amt = cardAmount(card);
  return amt == null ? null : formatCompactDollar(amt);
};

export const sumCardAmounts = (cards) =>
  (Array.isArray(cards) ? cards : []).reduce(
    (sum, c) => sum + (Number(cardAmount(c)) || 0),
    0,
  );

// ── Dates ────────────────────────────────────────────────────────────────────
export const dayKey = (date) => (date ? format(date, "yyyy-MM-dd") : null);

// Parse a feed date into a LOCAL Date on the calendar day it belongs to.
//   date-only / midnight ("2026-06-15", "2026-06-15T00:00:00") → the calendar
//   fields are authoritative: read them directly, never through `new Date(iso)`
//   (which would shift a negative-offset zone to the previous day).
//   a clock time (a preclose signing time) → a UTC instant like every other
//   timestamp the app stores; place it on its EASTERN day (the desk's zone),
//   so a 9 PM signing does not land on tomorrow's cell.
export const parseLocalDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "string") {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/);
    if (m) {
      const hasClock = m[4] != null && !(m[4] === "00" && m[5] === "00");
      if (hasClock) {
        const e = convertToEasternTime(normalizeIsoStringToUtc(value));
        if (e) {
          return new Date(e.getFullYear(), e.getMonth(), e.getDate(), e.getHours(), e.getMinutes());
        }
      }
      return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    }
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

// The day a card sits on: closed loans ride `closedDate`; everything else the
// funding date (scheduled → settlement → expected, per `dateBasis`).
export const cardDate = (card) =>
  parseLocalDate(card?.closed ? card?.closedDate || card?.fundingDate : card?.fundingDate);

// Whether the feed's funding date carries a clock (a preclose signing time).
export const cardHasTime = (card) =>
  typeof card?.fundingDate === "string" && /T\d{2}:\d{2}/.test(card.fundingDate) &&
  !/T00:00(:00)?$/.test(card.fundingDate);

export const prettyDate = (date) =>
  date ? format(date, "EEE, MMMM d, yyyy") : "";

// Bucket cards by their calendar day → Map<dayKey, card[]>, each day ordered by
// loan number. Cards with no date go to `unscheduled`.
export const bucketCardsByDay = (cards) => {
  const byDay = new Map();
  const unscheduled = [];
  (Array.isArray(cards) ? cards : []).forEach((card) => {
    const date = cardDate(card);
    if (!date) {
      unscheduled.push(card);
      return;
    }
    const key = dayKey(date);
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push(card);
  });
  byDay.forEach((list) =>
    list.sort((a, b) => (a?.loanNumber ?? 0) - (b?.loanNumber ?? 0)),
  );
  return { byDay, unscheduled };
};

// ── Attention chips ──────────────────────────────────────────────────────────
// The flags the doc says must be easy to spot at a glance. Returned in the
// order they should render; each carries its own tint.
export const cardAttentionChips = (card) => {
  if (!card) return [];
  const chips = [];
  if (card.closed) {
    chips.push({ key: "closed", label: closedLabel(card), c: slate[600], bg: slate[100] });
    return chips;
  }
  if (card.scheduledDatePassed) {
    chips.push({ key: "passed", label: "Date passed — reschedule", c: status.error.fg, bg: status.error.bg });
  }
  const missed = Array.isArray(card.missedScheduledDates) ? card.missedScheduledDates.length : 0;
  if (missed > 0) {
    chips.push({
      key: "missed",
      label: `Scheduled ${missed}× before, did not ${isTrade(card) ? "settle" : "close"}`,
      c: status.error.fg,
      bg: status.error.bg,
    });
  }
  if (card.stale) {
    chips.push({
      key: "stale",
      label: `Stale · ${card.daysSinceActivity ?? "?"}d quiet`,
      c: amber.chipText,
      bg: amber.light,
    });
  }
  if (card.ecdChurn) {
    chips.push({
      key: "churn",
      label: `ECD churn · ${card.ecdChangesInWindow ?? card.ecdChangeCount ?? ""}× moved`,
      c: amber.chipText,
      bg: amber.light,
    });
  }
  if (card.wireBlocked) {
    chips.push({ key: "wire", label: "Wire-blocked day", c: status.error.fg, bg: status.error.bg });
  }
  if (card.cutoffExceeded) {
    chips.push({ key: "cutoff", label: "Past cutoff", c: status.error.fg, bg: status.error.bg });
  }
  if (card.lockExpiresBeforeFunding) {
    chips.push({ key: "lock", label: "Lock expires before funding", c: status.error.fg, bg: status.error.bg });
  }
  if (card.closerActive) {
    chips.push({ key: "closer", label: "Closer active", c: status.success.fg, bg: status.success.bg });
  }
  return chips;
};

// Gating kind → tint (external = waiting on someone else; internal = ours).
export const GATING_KIND = {
  external: { label: "External", c: steel.main, bg: steel.tint },
  internal: { label: "Internal", c: teal[700], bg: teal[100] },
};
export const gatingKindMeta = (kind) => GATING_KIND[kind] || GATING_KIND.internal;

// Condition gate chip labels (Unspecified is treated as prior-to-funding and
// the UW is nudged to set it).
export const GATE_LABEL = {
  "Prior to Docs": "Prior to Docs",
  "Prior to Funding": "Prior to Funding",
  "Post Close": "Post Close",
};
export const gateLabel = (gate) => GATE_LABEL[gate] || "Unspecified";

// Document status → tint for the per-file chip.
export const docStatusMeta = (s) => {
  const v = String(s || "").toLowerCase();
  if (/accept/.test(v)) return { c: status.success.fg, bg: status.success.bg };
  if (/reject|fail/.test(v)) return { c: status.error.fg, bg: status.error.bg };
  if (/review|pending|processor/.test(v)) return { c: amber.chipText, bg: amber.light };
  return { c: slate[600], bg: slate[100] };
};

// Agreement status (funding-tape reconcile) → tint.
export const AGREEMENT = {
  agree: { label: "Agree", c: status.success.fg, bg: status.success.bg },
  disagree: { label: "Disagree", c: status.error.fg, bg: status.error.bg },
  los_only: { label: "LOS only", c: amber.chipText, bg: amber.light },
  doc_only: { label: "Document only", c: amber.chipText, bg: amber.light },
  no_source: { label: "No source", c: slate[500], bg: slate[100] },
  resolved: { label: "Resolved", c: steel.main, bg: steel.tint },
};
export const agreementMeta = (s) => AGREEMENT[s] || AGREEMENT.no_source;

// The full readiness object keeps the scheduling / activity / churn signals in
// nested blocks; the schedule CARD flattens them. Lift the same fields the
// backend's `card()` does so a surface can render either shape with the card
// accessors (dates, attention chips, counts).
export const readinessToCard = (r) => {
  if (!r) return r;
  const s = r.scheduling || {};
  const d = r.documents || {};
  const c = r.conditions || {};
  const e = r.exceptions || {};
  const la = r.lastActivity || {};
  const ecd = r.expectedDateChurn || {};
  const firm = r.firmDateChurn || {};
  return {
    ...r,
    mode: r.mode ?? s.mode,
    scheduledDate: s.scheduledDate,
    expectedDate: s.expectedDate,
    fundingDate: s.fundingDate,
    dateBasis: s.dateBasis,
    daysToFunding: s.daysToFunding,
    wireBlocked: s.wireBlocked,
    cutoffExceeded: s.cutoffExceeded,
    missingRequired: s.missingRequired,
    closed: s.closed,
    closedDate: s.closedDate,
    scheduledDatePassed: s.scheduledDatePassed,
    acceptedPct: d.acceptedPct,
    documentsVisible: d.totalVisible,
    documentsAccepted: d.accepted,
    openConditions: c.open,
    openPriorToFunding: c.openPriorToFunding,
    openExceptions: e.open,
    openHardExceptions: e.openHard,
    lockExpiresBeforeFunding: r.lock?.expiresBeforeFunding,
    lastActivityAt: la.mostRecent,
    stale: la.stale,
    daysSinceActivity: la.daysSinceMostRecent,
    closerLastActiveAt: la.closer?.lastAt,
    closerActive: la.closer?.active,
    closerEventsLast7Days: la.closer?.eventsLast7Days,
    ecdChangeCount: ecd.changeCount,
    ecdChangesInWindow: ecd.changesInWindow,
    ecdLastChangedAt: ecd.lastChangedAt,
    ecdChurn: ecd.churn,
    missedScheduledDates: firm.missedDates,
    aiHeadline: r.aiReview?.headline ?? null,
  };
};

// ── Search ───────────────────────────────────────────────────────────────────
// Free-text match over the fields a closer would type: loan #, sync id,
// borrower / loan name, address, statuses, closer, originator.
export const cardMatchesSearch = (card, q) => {
  const needle = String(q || "").trim().toLowerCase();
  if (!needle) return true;
  return [
    card?.loanName,
    card?.loanNumber,
    card?.syncLoanId,
    card?.loanStatus,
    card?.tradeStatus,
    card?.closer,
    card?.originator,
    cardDisplayName(card),
    ...(Array.isArray(card?.borrowerNames) ? card.borrowerNames : []),
    ...(Array.isArray(card?.propertyAddresses) ? card.propertyAddresses : []),
  ]
    .filter((v) => v != null)
    .some((v) => String(v).toLowerCase().includes(needle));
};

// Chip order inside a day: worst readiness first (the legend's order), then
// loan number; closed loans last.
export const compareCardsForDay = (a, b) => {
  const la = READINESS_ORDER.indexOf(a?.closed ? "closed" : a?.readinessLevel);
  const lb = READINESS_ORDER.indexOf(b?.closed ? "closed" : b?.readinessLevel);
  if (la !== lb) return (la < 0 ? 99 : la) - (lb < 0 ? 99 : lb);
  return (a?.loanNumber ?? 0) - (b?.loanNumber ?? 0);
};

// ── Calendar scope filters ───────────────────────────────────────────────────
// The schedule feed returns EVERY loan with a funding date in the window (any
// stage, closed included). The calendars narrow it by default to what the old
// "Clear To Close" calendar showed: loans in a funding stage, not yet closed.
// The status sets mirror the backend's (closing_readiness.py
// PRECLOSE_FUNDING_STATUSES / ACQUISITION_FUNDING_TRADE_STATUSES).
export const PRECLOSE_FUNDING_STATUSES = ["Clear To Close", "Closing"];
export const ACQUISITION_FUNDING_STATUSES = ["Cleared to Purchase", "Funding"];
export const ACQUISITION_FUNDING_TRADE_STATUSES = ["Committed", "Approved", "Funded"];

// True when the card is in a funding stage (closed loans are handled by the
// separate "show closed" toggle, so they are not counted here).
export const isFundingStage = (card) => {
  if (!card || card.closed) return false;
  if (isTrade(card)) {
    return (
      ACQUISITION_FUNDING_STATUSES.includes(card.loanStatus) ||
      ACQUISITION_FUNDING_TRADE_STATUSES.includes(card.tradeStatus)
    );
  }
  return PRECLOSE_FUNDING_STATUSES.includes(card.loanStatus);
};

export const STAGE_FILTERS = [
  { value: "funding", label: "Clear To Close only" },
  { value: "all", label: "All stages" },
];

// Apply the calendar scope: stage filter + closed toggle.
export const scopeCards = (cards, { stage = "funding", showClosed = false } = {}) =>
  (Array.isArray(cards) ? cards : []).filter((c) => {
    if (c?.closed) return showClosed;
    return stage === "all" || isFundingStage(c);
  });

// ── Scheduling entry → where it is edited on the loan file ──────────────────
// A Scheduling entries row offers "go to" the drilldown spot that owns the
// field. `tab` is the drilldown route segment ("" = the Summary landing tab);
// the target row carries `data-drilldown-field="<field>"`, which
// useDrilldownFieldFocus scrolls to and flashes. An entry with no editor on
// the loan file (e.g. trade status) has no destination.
const LOAN_INFO_CLOSING = { tab: "loan-information", label: "Loan Info → Closing & Funding" };
const LOAN_INFO_SETTLEMENT = { tab: "loan-information", label: "Loan Info → Closing & Settlement" };
const SUMMARY_TEAM = { tab: "", label: "Summary → Assigned team" };

const SCHEDULE_FIELD_DESTINATIONS = {
  preclose: {
    closingScheduled: LOAN_INFO_CLOSING,
    closingScheduledDate: LOAN_INFO_CLOSING,
    expectedClosingDate: LOAN_INFO_CLOSING,
    closingCutoffDate: LOAN_INFO_CLOSING,
    closingStatus: LOAN_INFO_CLOSING,
    wireStatus: LOAN_INFO_CLOSING,
    wireSentDate: LOAN_INFO_CLOSING,
    loanCloser: SUMMARY_TEAM,
  },
  acquisition: {
    expectedClosingDate: LOAN_INFO_SETTLEMENT,
    expectedSettlementDate: LOAN_INFO_SETTLEMENT,
    settlementDate: LOAN_INFO_SETTLEMENT,
    commitmentDate: LOAN_INFO_SETTLEMENT,
    wireReleaseDate: LOAN_INFO_SETTLEMENT,
    fundingWireAmount: LOAN_INFO_SETTLEMENT,
  },
};

export const scheduleFieldDestination = (field, readiness) =>
  SCHEDULE_FIELD_DESTINATIONS[isTrade(readiness) ? "acquisition" : "preclose"][field] || null;

// The loan file's URL for a destination. Inside the drilldown the current
// base is kept (it may be embedded, e.g. under the Lock Queue); elsewhere it
// is the pipeline's own route.
export const scheduleFieldHref = ({ dealId, readiness, pathname, field }) => {
  const dest = scheduleFieldDestination(field, readiness);
  if (!dest || !dealId) return null;
  const marker = `/${dealId}`;
  const idx = (pathname || "").indexOf(marker);
  const base =
    idx === -1
      ? `${isTrade(readiness) ? "/acquisition" : "/loan"}${marker}`
      : pathname.slice(0, idx + marker.length);
  return `${base}${dest.tab ? `/${dest.tab}` : ""}?focus=${encodeURIComponent(field)}`;
};
